// src/components/FullCalendar/FullCalendarUtils.js
import moment from "moment";

export const ISO_FMT = "YYYY-MM-DD";
export const MAX_DAYS_PER_CYCLE = 15; // Default for regular employees
export const MAX_DAYS_COMERCIAL = 20; // Comercial team gets 20 days

// Helper function to get max days based on employee type.
// The commercial flag can come from AG_EMPLOYEES, USUARIOS_MONITOR_NOGLE,
// /calendar/me, or frontend-normalized payloads.
export const getMaxDaysForEmployee = (employee) => {
  if (!employee) return MAX_DAYS_PER_CYCLE;

  const comercialField =
    employee.is_comercial_AG_EMPLOYEES ??
    employee.IS_COMERCIAL_AG_EMPLOYEES ??
    employee.is_comercial_USUARIOS_MONITOR_NOGLE ??
    employee.IS_COMERCIAL_USUARIOS_MONITOR_NOGLE ??
    employee.isComercial ??
    employee.IS_COMERCIAL ??
    employee.is_comercial ??
    employee.IS_COMERCIAL_EMPLOYEE ??
    employee.comercial ??
    employee.COMERCIAL ??
    0;

  const normalized = String(comercialField).toLowerCase().trim();
  const asNumber = Number(comercialField);
  const isComercial =
    comercialField === true ||
    normalized === "true" ||
    normalized === "sim" ||
    normalized === "yes" ||
    (!Number.isNaN(asNumber) && asNumber >= 1);

  return isComercial ? MAX_DAYS_COMERCIAL : MAX_DAYS_PER_CYCLE;
};

// Sprint é um marcador global de virada de sprint, não uma ausência:
// nunca deve contar em férias, folgas ou relatórios.
export const isSprintCategory = (category) =>
  String(category || "").toLowerCase().trim() === "sprint";

// Número da virada de sprint: vem do comentário ("Sprint N[: texto]").
// O título não é confiável — na visão admin ele é "nome - categoria", sem o número.
export const getSprintNumber = (comment, title) => {
  const match =
    String(comment || "").match(/Sprint\s+(\d+)/i) ||
    String(title || "").match(/Sprint\s+(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
};

// Apenas Férias consome o limite do ciclo (com/sem acento)
export const isFeriasCategory = (category) => {
  const c = String(category || "").toLowerCase().trim();
  return c.includes("férias") || c.includes("ferias");
};

export const getIsAdminFromRecord = (record) => {
  if (!record) return false;

  const adminField =
    record.IS_ADMIN ??
    record.isAdmin ??
    record.is_admin ??
    record.is_admin_USUARIOS_MONITOR_NOGLE ??
    record.IS_ADMIN_USUARIOS_MONITOR_NOGLE ??
    0;

  const normalized = String(adminField).toLowerCase().trim();
  const asNumber = Number(adminField);

  return (
    adminField === true ||
    normalized === "true" ||
    normalized === "sim" ||
    normalized === "yes" ||
    (!Number.isNaN(asNumber) && asNumber >= 1)
  );
};
export const toISO = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m.format(ISO_FMT) : null;
  }

  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  const m = moment(s);
  return m.isValid() ? m.format(ISO_FMT) : null;
};

export const fmtBR = (iso) => {
  if (!iso) return "—";
  const m = moment(String(iso).slice(0, 10), ISO_FMT, true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(iso);
};

export const stripEmailDomain = (value) => {
  if (!value) return "";
  const s = String(value);
  const at = s.indexOf("@");
  return at > 0 ? s.slice(0, at) : s;
};

export const addYearsISO = (iso, years) => {
  const d = moment(String(iso).slice(0, 10), ISO_FMT, true);
  if (!d.isValid()) return null;
  return d.clone().add(Number(years || 0), "year").format(ISO_FMT);
};

export const inclusiveDaysCount = (startISO, endISO) => {
  const s = moment(startISO, ISO_FMT, true);
  const e = moment(endISO, ISO_FMT, true);
  if (!s.isValid() || !e.isValid()) return 0;
  return e.diff(s, "days") + 1;
};

export const overlapDaysInclusive = (aStartISO, aEndISO, bStartISO, bEndISO) => {
  const s = moment(aStartISO, ISO_FMT, true).startOf("day");
  const e = moment(aEndISO, ISO_FMT, true).startOf("day");
  const bs = moment(bStartISO, ISO_FMT, true).startOf("day");
  const be = moment(bEndISO, ISO_FMT, true).startOf("day");

  if (!s.isValid() || !e.isValid() || !bs.isValid() || !be.isValid()) return 0;

  const start = moment.max(s, bs);
  const end = moment.min(e, be);

  if (end.isBefore(start, "day")) return 0;
  return end.diff(start, "days") + 1;
};

// Aliases for compatibility if needed, or just use overlapDaysInclusive
export const overlapDays = overlapDaysInclusive;

// ✅ ciclo anual baseado na data de admissão (com ciclo inicial antes de 1 ano)
export const getAdmissionCycleWindow = (admissionISO, referenceISO) => {
  const target = moment(referenceISO || moment().format(ISO_FMT), ISO_FMT, true).startOf("day");
  if (!target.isValid()) {
    const y = moment().year();
    return {
      hasAdmission: false,
      cycleStartISO: `${y}-01-01`,
      cycleEndISO: `${y}-12-31`,
      nextCycleStartISO: `${y + 1}-01-01`,
      isInitialCyclePending: false,
      firstAnniversaryISO: null,
    };
  }

  const adm = moment(admissionISO || "", ISO_FMT, true).startOf("day");
  if (!adm.isValid()) {
    const y = target.year();
    return {
      hasAdmission: false,
      cycleStartISO: `${y}-01-01`,
      cycleEndISO: `${y}-12-31`,
      nextCycleStartISO: `${y + 1}-01-01`,
      isInitialCyclePending: false,
      firstAnniversaryISO: null,
    };
  }

  const firstAnniversary = adm.clone().add(1, "year").startOf("day");

  // ciclo inicial: admissão → (admissão + 1 ano - 1 dia)
  if (target.isBefore(firstAnniversary, "day")) {
    return {
      hasAdmission: true,
      cycleStartISO: adm.format(ISO_FMT),
      cycleEndISO: firstAnniversary.clone().subtract(1, "day").format(ISO_FMT),
      nextCycleStartISO: firstAnniversary.format(ISO_FMT),
      isInitialCyclePending: true,
      firstAnniversaryISO: firstAnniversary.format(ISO_FMT),
    };
  }

  // ciclos seguintes: aniversário (mês/dia da admissão)
  const m = adm.month();
  const d = adm.date();
  const y = target.year();

  let cycleStart = moment({ year: y, month: m, date: d }).startOf("day");

  // fallback (ex.: 29/02 em ano não-bissexto)
  if (!cycleStart.isValid()) {
    const lastDay = moment({ year: y, month: m, date: 1 }).endOf("month").date();
    cycleStart = moment({ year: y, month: m, date: Math.min(d, lastDay) }).startOf("day");
  }

  if (target.isBefore(cycleStart, "day")) {
    cycleStart = cycleStart.clone().subtract(1, "year");
  }

  const nextCycleStart = cycleStart.clone().add(1, "year").startOf("day");
  const cycleEnd = nextCycleStart.clone().subtract(1, "day").startOf("day");

  return {
    hasAdmission: true,
    cycleStartISO: cycleStart.format(ISO_FMT),
    cycleEndISO: cycleEnd.format(ISO_FMT),
    nextCycleStartISO: nextCycleStart.format(ISO_FMT),
    isInitialCyclePending: false,
    firstAnniversaryISO: firstAnniversary.format(ISO_FMT),
  };
};

export const calcDaysToNextCycle = (nextCycleISO, referenceISO) => {
  const ref = moment(referenceISO || moment().format(ISO_FMT), ISO_FMT, true).startOf("day");
  const nxt = moment(nextCycleISO || "", ISO_FMT, true).startOf("day");
  if (!ref.isValid() || !nxt.isValid()) return null;
  return nxt.diff(ref, "days");
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const formatViewTitle = (title) => {
  if (!title) return "";

  let out = String(title);

  const monthsFull = [
    "janeiro",
    "fevereiro",
    "março",
    "abril",
    "maio",
    "junho",
    "julho",
    "agosto",
    "setembro",
    "outubro",
    "novembro",
    "dezembro",
  ];

  const monthsShort = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

  [...monthsFull, ...monthsShort].forEach((m) => {
    const rx = new RegExp(`\\b${escapeRegExp(m)}\\b`, "gi");
    out = out.replace(rx, (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase());
  });

  if (/^[a-záàâãéêíóôõúç]/i.test(out)) out = out.charAt(0).toUpperCase() + out.slice(1);

  return out;
};

// =========================================================
// ✅ Category Colors
// =========================================================
// Férias=Verde, Evento=Roxo, DayOff=Azul, Folga=Amarelo
export const CATEGORY_COLORS = {
  "Férias": "#02ea75",
  "Evento": "#805ad5",
  "DayOff": "#3182ce",
  "Folga": "#ecc94b",
  "Sprint": "#d53f8c",
};

export const getCategoryColor = (category) => {
  if (!category) return CATEGORY_COLORS["Férias"];
  const key = Object.keys(CATEGORY_COLORS).find(
    (k) => k.toLowerCase() === String(category).toLowerCase()
  );
  return key ? CATEGORY_COLORS[key] : CATEGORY_COLORS["Férias"];
};

// =========================================================
// ✅ Color Mapping (1-10)
// =========================================================
export const PRESET_COLORS = [
  "#02ea75", // 1: Green (Default)
  "#3182ce", // 2: Blue
  "#ecc94b", // 3: Yellow
  "#e53e3e", // 4: Red
  "#805ad5", // 5: Purple
  "#d69e2e", // 6: Orange
  "#38a169", // 7: Dark Green
  "#d53f8c", // 8: Pink
  "#0bc5ea", // 9: Cyan
  "#fefcbf", // 10: Light Yellow
];

export const getColorById = (id) => {
  // Handle null/undefined -> Default Green
  if (id == null) return PRESET_COLORS[0];

  // If it's already a hex string (legacy support), return it
  if (String(id).startsWith("#")) return id;

  const index = parseInt(id, 10) - 1;
  if (index >= 0 && index < PRESET_COLORS.length) {
    return PRESET_COLORS[index];
  }
  return PRESET_COLORS[0]; // Default to Green
};

export const getIdByColor = (color) => {
  if (!color) return 1;
  const index = PRESET_COLORS.indexOf(color);
  return index !== -1 ? index + 1 : 1; // Default to 1
};
